import { Box, Stack, TableCell, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Juicio, Nivel } from '../lib/api.js';

/** Cómo se ve un juicio de `@film/shared/idoneidad` (D-21).
 *
 *  El color va SIEMPRE acompañado de un símbolo y de un motivo en el tooltip. Una tabla
 *  que solo se entienda por el color no se entiende con ocho por ciento de la gente
 *  —ni en una captura en blanco y negro—, y un rojo sin explicación obliga a abrir
 *  Ajustes para recordar qué se pidió. */

const PALETA: Record<Nivel, 'success' | 'warning' | 'error' | null> = {
  ideal: 'success',
  aceptable: 'warning',
  rechazada: 'error',
  // Sin color: «el sitio no lo dice» no es un cuarto grado de bondad.
  desconocida: null,
};

const SIMBOLO: Record<Nivel, string> = {
  ideal: '✓',
  aceptable: '≈',
  rechazada: '✗',
  desconocida: '?',
};

export function colorDeNivel(nivel: Nivel): string {
  const p = PALETA[nivel];
  return p ? `${p}.main` : 'text.disabled';
}

/** Una celda con su color, su símbolo y su porqué.
 *
 *  El fondo es tenue y la marca fuerte es el borde izquierdo: cinco celdas con fondo
 *  saturado dejan la fila ilegible, que era justo lo que se venía a arreglar. */
export function CeldaIdoneidad({
  juicio,
  valor,
  detalle,
  align,
}: {
  juicio: Juicio;
  valor: React.ReactNode;
  /** Segunda línea, más pequeña: el texto crudo del sitio cuando lo hay. */
  detalle?: React.ReactNode;
  align?: 'left' | 'right';
}) {
  const p = PALETA[juicio.nivel];
  return (
    <Tooltip title={juicio.motivo}>
      <TableCell
        sx={{
          borderLeft: 3,
          borderLeftColor: p ? `${p}.main` : 'transparent',
          bgcolor: (t) => (p ? alpha(t.palette[p].main, 0.09) : 'transparent'),
          whiteSpace: 'nowrap',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: 'baseline', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
        >
          {/* Decorativo: lo que dice el nivel de verdad es el tooltip, y repetirlo aquí
              haría que un lector de pantalla leyera un símbolo sin significado. */}
          <Box component="span" aria-hidden sx={{ color: colorDeNivel(juicio.nivel), fontSize: 13 }}>
            {SIMBOLO[juicio.nivel]}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" component="div">{valor}</Typography>
            {detalle && (
              <Typography variant="caption" color="text.secondary" component="div">
                {detalle}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>
    </Tooltip>
  );
}
